import React, { useEffect, useState } from "react";
import { DatePicker } from "@/components/DatePicker";
import { formatDate } from "@/lib/dateFormat";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Bell, Trash2 } from "lucide-react";

export default function RemindersPanel({ refillRequired }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({
    type: "water_care",
    title: "",
    due_date: new Date().toISOString().slice(0, 10),
    interval_days: "",
  });

  const load = () => api.get("/reminders").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (f.type === "refill" && !refillRequired) {
      toast("Nachfüllen: nicht meldepflichtig", { description: t("refill_report_required_hint") });
    }
    await api.post("/reminders", {
      type: f.type,
      title: f.title || t(f.type),
      due_date: f.due_date,
      interval_days: f.interval_days ? parseInt(f.interval_days) : null,
    });
    toast.success(t("reminder_added"));
    setF({ type: "water_care", title: "", due_date: new Date().toISOString().slice(0, 10), interval_days: "" });
    load();
  };

  const toggle = async (id) => {
    await api.patch(`/reminders/${id}/toggle`);
    load();
  };
  const del = async (id) => {
    await api.delete(`/reminders/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t("add_reminder")}</h3>
          </div>
          <form className="grid gap-4 sm:grid-cols-4" onSubmit={submit}>
            <div>
              <Label>{t("category")}</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger data-testid="rem-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="water_care">{t("water_care")}</SelectItem>
                  <SelectItem value="backwash">{t("backwash")}</SelectItem>
                  <SelectItem value="refill">{t("refill")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("due_date")}</Label>
              <DatePicker value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} testId="rem-date-input" />
            </div>
            <div>
              <Label>{t("interval_days")}</Label>
              <Input type="number" min="0" value={f.interval_days} onChange={(e) => setF({ ...f, interval_days: e.target.value })} data-testid="rem-interval-input" />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" data-testid="rem-submit-btn">
                {t("save")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">{t("upcoming")}</h3>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("no_data")}</div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((r) => (
                <li key={r.id} className="flex items-center gap-3 py-3">
                  <Checkbox checked={r.done} onCheckedChange={() => toggle(r.id)} data-testid={`rem-check-${r.id}`} />
                  <div className="flex-1">
                    <div className={`font-medium ${r.done ? "line-through text-muted-foreground" : ""}`}>
                      {r.title}
                    </div>
                    <div className="label-caps mt-0.5">
                      {t(r.type)} · {formatDate(r.due_date)}
                      {r.interval_days ? ` · ${r.interval_days}d` : ""}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`rem-del-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
